import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  FiPlus,
  FiLayers,
  FiCheckCircle,
  FiGrid,
  FiList,
  FiChevronDown,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiTrash2
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { get, del } from "../../services/ApiService";
import Header from "../../components/Header/Header";
import "./Dashboard.css";
import Modal from "../../components/Modal/Modal";
import { showToast } from "../../ui/toast";

const PREF_KEY_BASE = "buglog:dashboard:prefs";

const prefsKey = (companyName) => {
  const firm = companyName ? decodeURIComponent(companyName) : "__all__";
  return `${PREF_KEY_BASE}:${encodeURIComponent(firm)}`;
};

const readPrefs = (companyName) => {
  try {
    const raw = localStorage.getItem(prefsKey(companyName)) || "";
    if (!raw) {
      return {
        view: "grid",
        sort: "recent",
        onlyOpen: false,
        onlyWorked: false,
        query: "",
        page: 1
      };
    }
    const obj = JSON.parse(raw);
    return {
      view: obj?.view === "list" ? "list" : "grid",
      sort: obj?.sort === "az" ? "az" : obj?.sort === "za" ? "za" : "recent",
      onlyOpen: !!obj?.onlyOpen,
      onlyWorked: !!obj?.onlyWorked,
      query: typeof obj?.query === "string" ? obj.query : "",
      page: Number.isFinite(Number(obj?.page)) ? Math.max(1, Number(obj.page)) : 1
    };
  } catch {
    return {
      view: "grid",
      sort: "recent",
      onlyOpen: false,
      onlyWorked: false,
      query: "",
      page: 1
    };
  }
};

const writePrefs = (companyName, next) => {
  try {
    localStorage.setItem(prefsKey(companyName), JSON.stringify(next));
  } catch { }
};

const toBool = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "yes";

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const readCurrentUserId = () =>
  normalizeId(
    localStorage.getItem("user_id") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("uid") ||
      Cookies.get("user_id") ||
      Cookies.get("uid") ||
      ""
  );

const canManageProject = (project) => {
  const currentUserId = readCurrentUserId();
  const creatorId = normalizeId(
    project?.created_by ??
      project?.created_by_id ??
      project?.owner_id ??
      project?.user_id
  );
  const rawRole = String(
    project?.role ??
      project?.member_role ??
      project?.project_role ??
      project?.permission ??
      ""
  )
    .trim()
    .toLowerCase();

  if (
    toBool(project?.is_owner) ||
    toBool(project?.owner) ||
    toBool(project?.is_admin) ||
    toBool(project?.admin) ||
    toBool(project?.is_manager) ||
    toBool(project?.manager)
  ) {
    return true;
  }

  if (["owner", "admin", "administrator", "manager", "yönetici"].includes(rawRole)) {
    return true;
  }

  if (currentUserId && creatorId && currentUserId === creatorId) {
    return true;
  }

  return false;
};

const CalendarStrip = ({ events }) => {
  const container = useRef(null);
  const today = new Date();

  const pastCount = 7;
  const futureCount = 14;

  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  start.setDate(today.getDate() - pastCount);

  const todayMid = new Date(today);
  todayMid.setHours(0, 0, 0, 0);

  const keyOf = (d) => d.toISOString().slice(0, 10);

  const days = Array.from({ length: pastCount + 1 + futureCount }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });

  const scroll = (dir) => {
    if (!container.current) return;
    container.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <section className="cal-strip-wrapper">
      <button className="cal-nav left" onClick={() => scroll(-1)} type="button">
        <FiChevronLeft size={18} />
      </button>
      <div className="cal-strip" ref={container}>
        {days.map((d, i) => {
          const key = keyOf(d);
          const count = events[key] || 0;
          const isToday = key === keyOf(today);
          const isPast = d < todayMid && !isToday;
          return (
            <div
              key={i}
              className={`day-chip ${isToday ? "today" : ""} ${count ? "has" : ""} ${isPast ? "past" : ""}`}
              title={d.toLocaleDateString("tr-TR")}
            >
              <span className="day">
                {d.toLocaleString("tr-TR", { weekday: "short" })}
              </span>
              <span className="date">{d.getDate()}</span>
              {count > 0 && <span className="cnt">{count}</span>}
            </div>
          );
        })}
      </div>
      <button className="cal-nav right" onClick={() => scroll(1)} type="button">
        <FiChevronRight size={18} />
      </button>
    </section>
  );
};

const getCover = (uniq) => {
  try {
    return uniq ? localStorage.getItem(`cover:${uniq}`) || "" : "";
  } catch {
    return "";
  }
};

const getProjectTitle = (p) => {
  try {
    const key = p?.uniq || p?.id;
    const ls = key ? localStorage.getItem(`title:${key}`) || "" : "";
    if (ls && String(ls).trim()) return String(ls).trim();
  } catch { }
  return p?.name || "";
};

const getProjectColor = (p) => {
  try {
    const ls = p?.uniq ? localStorage.getItem(`color:${p.uniq}`) : "";
    return ls || p?.color || "";
  } catch {
    return p?.color || "";
  }
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return hex;
  let h = hex.trim();
  if (h.startsWith("rgba(") || h.startsWith("rgb(")) return h;
  if (h[0] === "#") h = h.slice(1);
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return hex;
};

const PAGE_SIZE = 12;

const getDeadline = (p) =>
  p?.deadline || p?.deadline_date || p?.deadline_at || p?.end_date || null;

const isPastDate = (value) => {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(23, 59, 59, 999);
  return d.getTime() < Date.now();
};

const formatTR = (val) => {
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
};

const NO_COMPANY_LABEL = "Firma Yok";

const firmNameOf = (p) => {
  const fromApi =
    p?.company_name ||
    p?.company ||
    p?.firm ||
    p?.organization ||
    p?.org ||
    p?.client ||
    "";
  if (fromApi && String(fromApi).trim()) return String(fromApi).trim();
  try {
    const key = p?.uniq || p?.id;
    const ls = key ? localStorage.getItem(`project-company:${key}`) || "" : "";
    if (ls && String(ls).trim()) return String(ls).trim();
  } catch { }
  return "";
};

const firmDisplayName = (p) => {
  const n = firmNameOf(p);
  return n ? n : NO_COMPANY_LABEL;
};

const createdAtMs = (p) => {
  const v = p?.created_at || p?.createdAt || p?.created || "";
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return 0;
  return d.getTime();
};

const lastWorkMs = (p) => {
  const v =
    p?.last_bug_date ||
    p?.last_bug_at ||
    p?.last_activity_at ||
    p?.last_job_date ||
    "";
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return 0;
  return d.getTime();
};

const hasAnyWork = (p) => {
  const total = Number(p?.total_bugs || 0);
  if (total > 0) return true;
  if (lastWorkMs(p) > 0) return true;
  return false;
};

const Dashboard = () => {
  const nav = useNavigate();
  const { companyName } = useParams();

  const initialPrefs = useMemo(() => readPrefs(companyName), [companyName]);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState(() => initialPrefs.view);
  const [sort, setSort] = useState(() => initialPrefs.sort);
  const [onlyOpen, setOnlyOpen] = useState(() => initialPrefs.onlyOpen);
  const [onlyWorked, setOnlyWorked] = useState(() => initialPrefs.onlyWorked);
  const [query, setQuery] = useState(() => initialPrefs.query);
  const [page, setPage] = useState(() => initialPrefs.page);

  const [deletingId, setDeletingId] = useState(null);

  const [confirmDlg, setConfirmDlg] = useState({
    open: false,
    project: null
  });

  const [titleTick, setTitleTick] = useState(0);

  useEffect(() => {
    const onChanged = () => {
      setTitleTick((x) => x + 1);
    };
    const onStorage = (e) => {
      if (typeof e?.key === "string" && e.key.startsWith("title:")) {
        setTitleTick((x) => x + 1);
      }
      if (e?.key === prefsKey(companyName)) {
        const p = readPrefs(companyName);
        setView(p.view);
        setSort(p.sort);
        setOnlyOpen(p.onlyOpen);
        setOnlyWorked(p.onlyWorked);
        setQuery(p.query);
        setPage(p.page);
      }
    };
    window.addEventListener("project:changed", onChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("project:changed", onChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [companyName]);

  useEffect(() => {
    writePrefs(companyName, { view, sort, onlyOpen, onlyWorked, query, page });
  }, [companyName, view, sort, onlyOpen, onlyWorked, query, page]);

  useEffect(() => {
    const p = readPrefs(companyName);
    setView(p.view);
    setSort(p.sort);
    setOnlyOpen(p.onlyOpen);
    setOnlyWorked(p.onlyWorked);
    setQuery(p.query);
    setPage(p.page);
  }, [companyName]);

  const fetchAll = async () => {
    setLoading(true);
    const res = await get("/projects");
    if (res?.status === "success" && Array.isArray(res.projects)) {
      setProjects(res.projects);
    } else {
      setProjects([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const firmFilter = useMemo(
    () => (companyName ? decodeURIComponent(companyName) : null),
    [companyName]
  );

  const effectiveProjects = useMemo(() => {
    if (!firmFilter) return projects;
    return projects.filter((p) => firmDisplayName(p) === firmFilter.trim());
  }, [projects, firmFilter]);

  const stats = useMemo(
    () => ({
      total: effectiveProjects.length,
      open: effectiveProjects.reduce((a, p) => a + (p.incomplete_bugs_count || 0), 0),
      done: effectiveProjects.reduce(
        (a, p) => a + ((p.incomplete_bugs_count || 0) ? 0 : 1),
        0
      )
    }),
    [effectiveProjects]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return effectiveProjects.filter((p) => {
      const name = getProjectTitle(p);
      const openOk = !onlyOpen || (p.incomplete_bugs_count || 0);
      const workedOk = !onlyWorked || hasAnyWork(p);

      const queryOk =
        q === "" ||
        String(name || "").toLowerCase().includes(q) ||
        String(p.description || "").toLowerCase().includes(q);

      return openOk && workedOk && queryOk;
    });
  }, [effectiveProjects, onlyOpen, onlyWorked, query, titleTick]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      const an = getProjectTitle(a) || "";
      const bn = getProjectTitle(b) || "";

      if (onlyWorked) {
        const bw = lastWorkMs(b);
        const aw = lastWorkMs(a);
        if (bw !== aw) return bw - aw;

        const bc = createdAtMs(b);
        const ac = createdAtMs(a);
        if (bc !== ac) return bc - ac;

        return bn.localeCompare(an);
      }

      if (sort === "az") return an.localeCompare(bn);
      if (sort === "za") return bn.localeCompare(an);
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return list;
  }, [filtered, sort, onlyWorked, titleTick]);

  useEffect(() => {
    setPage(1);
  }, [query, onlyOpen, onlyWorked, sort, view, firmFilter]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const paginated = sorted.slice(start, end);

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [safePage, page]);

  const initials = (n) =>
    (n || "")
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

  const events = useMemo(() => {
    const map = {};
    effectiveProjects.forEach((p) => {
      const key = (p.last_bug_date || p.created_at || "").slice(0, 10);
      if (!key) return;
      const inc = p.incomplete_bugs_count || 0;
      map[key] = (map[key] || 0) + inc;
    });
    return map;
  }, [effectiveProjects]);

  const askDeleteProject = (project) => {
    setConfirmDlg({ open: true, project });
  };

  const handleDeleteProject = async (project) => {
    if (!project?.uniq) {
      alert("Projeye ait uniq değeri bulunamadı. Silme işlemi için \"uniq\" gerekiyor.");
      return;
    }

    const uniq = project.uniq;
    setDeletingId(uniq);

    const prev = projects;
    setProjects((p) => p.filter((x) => x.uniq !== uniq));

    const res = await del("/projects", { project_id: uniq });
    setDeletingId(null);

    if (res?.status !== "success") {
      setProjects(prev);
      showToast({
        title: "Silme başarısız",
        description: res?.message || "Proje silinemedi.",
        variant: "error"
      });
    } else {
      try {
        localStorage.removeItem(`cover:${uniq}`);
        localStorage.removeItem(`title:${uniq}`);
      } catch { }
      showToast({
        title: "Proje silindi",
        description: `“${getProjectTitle(project)}” kaldırıldı.`,
        variant: "success"
      });
    }
  };

  const goPage = (n) => {
    const next = Math.min(Math.max(1, n), totalPages);
    if (next !== page) {
      setPage(next);
      try {
        document
          .querySelector(".dash-list")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch { }
    }
  };

  const visiblePages = useMemo(() => {
    const pages = [];
    const windowSize = 1;
    const add = (n) => pages.push(n);
    add(1);
    for (
      let i = Math.max(2, page - windowSize);
      i <= Math.min(totalPages - 1, page + windowSize);
      i++
    ) {
      add(i);
    }
    if (totalPages > 1) add(totalPages);
    return [...new Set(pages)].sort((a, b) => a - b);
  }, [page, totalPages]);

  const hasAnyOverdue = useMemo(
    () =>
      effectiveProjects.some((p) => {
        const dl = getDeadline(p);
        return dl && isPastDate(dl) && (p.incomplete_bugs_count || 0) > 0;
      }),
    [effectiveProjects]
  );

  useEffect(() => {
    const root = document.documentElement;
    if (hasAnyOverdue) {
      root.classList.add("theme-overdue");
    } else {
      root.classList.remove("theme-overdue");
    }
    return () => root.classList.remove("theme-overdue");
  }, [hasAnyOverdue]);

  return (
    <>
      <Header buttons={[]} />
      <main className="dash">
        <section className="dash-hero">
          <div className="dash-hero-copy">
            <span className="dash-hero-kicker">
              {firmFilter ? "Firma Görünümü" : "Buglog Dashboard"}
            </span>
            <h1>{firmFilter ? `${firmFilter} projeleri` : "Projelerini tek akışta yönet"}</h1>
            <p>
              {firmFilter
                ? `${firmFilter} için ${stats.total} proje arasından filtrelenmiş görünümü izliyorsun.`
                : "Açık işler, teslim tarihleri ve proje durumu tek merkezde toplandı."}
            </p>

            <div className="dash-hero-tags">
              <span className="dash-hero-tag">Toplam {stats.total} proje</span>
              <span className="dash-hero-tag">{sorted.length} görünümde</span>
              <span className="dash-hero-tag">{stats.open} açık iş</span>
              {firmFilter && (
                <button
                  className="dash-hero-link"
                  onClick={() => nav("/companies")}
                  title="Firmalar listesine dön"
                  type="button"
                >
                  Firmalar
                </button>
              )}
            </div>
          </div>

          <div className="dash-hero-panel">
            <div className="dash-hero-metric">
              <strong>{stats.done}</strong>
              <span>Tamamlanan Proje</span>
            </div>
            <div className="dash-hero-metric">
              <strong>{totalPages}</strong>
              <span>Sayfa</span>
            </div>
            <button className="dash-hero-action" onClick={() => nav("/add-project")} type="button">
              <FiPlus size={18} />
              <span>Yeni Proje</span>
            </button>
          </div>
        </section>

        <section className="dash-stats">
          <div className="stat-card">
            <FiLayers size={22} />
            <span>Proje</span>
            <strong>{stats.total}</strong>
          </div>
          <div className="stat-card">
            <FiPlus size={22} />
            <span>Açık İş</span>
            <strong>{stats.open}</strong>
          </div>
          <div className="stat-card">
            <FiCheckCircle size={22} />
            <span>Tamamlanan</span>
            <strong>{stats.done}</strong>
          </div>
        </section>

        <CalendarStrip events={events} />

        <section className="dash-toolbar">
          <div className="search-box">
            <FiSearch size={16} />
            <input
              placeholder="Projede ara..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="view-toggle">
            <button
              className={view === "grid" ? "active" : ""}
              onClick={() => setView("grid")}
              type="button"
            >
              <FiGrid size={18} />
            </button>
            <button
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
              type="button"
            >
              <FiList size={18} />
            </button>
          </div>

          <div className="chips">
            <label
              className={`chip ${!onlyOpen && !onlyWorked ? "chosen" : ""}`}
              onClick={() => {
                setOnlyOpen(false);
                setOnlyWorked(false);
              }}
            >
              Hepsi
            </label>
            <label
              className={`chip ${onlyOpen && !onlyWorked ? "chosen" : ""}`}
              onClick={() => {
                setOnlyOpen(true);
                setOnlyWorked(false);
              }}
            >
              Açık İş
            </label>
            <label
              className={`chip ${onlyWorked ? "chosen" : ""}`}
              onClick={() => {
                setOnlyOpen(false);
                setOnlyWorked(true);
              }}
            >
              En Son Eklenen
            </label>
          </div>

          <div className="sort-box">
            <select value={sort} onChange={(e) => setSort(e.target.value)} disabled={onlyWorked}>
              <option value="recent">En Yeni</option>
              <option value="az">A-Z</option>
              <option value="za">Z-A</option>
            </select>
            <FiChevronDown size={16} />
          </div>
        </section>

        <section className={`dash-list ${view}`}>
          {loading
            ? Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div key={i} className="dash-card skeleton" />
            ))
            : paginated.length === 0
              ? (
                <div className="dash-empty">
                  <strong>Bu görünümde proje bulunamadı</strong>
                  <p>Arama ve filtreleri değiştirerek tekrar deneyebilir ya da yeni proje ekleyebilirsin.</p>
                  <button type="button" onClick={() => nav("/add-project")}>
                    <FiPlus size={16} />
                    <span>Yeni Proje</span>
                  </button>
                </div>
              )
            : paginated.map((p, i) => {
              const name = getProjectTitle(p);

              const progress =
                (p.incomplete_bugs_count || 0) === 0
                  ? 100
                  : Math.max(
                    0,
                    100 -
                    ((p.incomplete_bugs_count || 0) / (p.total_bugs || 1)) * 100
                  );

              const cover = getCover(p.uniq);
              const color = getProjectColor(p);
              const badgeBg = color ? hexToRgba(color, 0.12) : undefined;
              const badgeBorder = color ? hexToRgba(color, 0.35) : undefined;

              const deadline = getDeadline(p);
              const overdue =
                deadline && isPastDate(deadline) && (p.incomplete_bugs_count || 0) > 0;

              return (
                <article
                  key={p.id ?? p.uniq}
                  className={`dash-card appear ${overdue ? "overdue" : ""}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  onClick={() =>
                    nav(`/bugs/${p.uniq}/${canManageProject(p) ? 1 : 0}/${encodeURIComponent(name)}`)
                  }
                >
                  <div
                    className={`dash-logo ${cover ? "has-cover" : ""}`}
                    style={
                      cover
                        ? { backgroundImage: `url(${cover})` }
                        : color
                          ? { background: `linear-gradient(135deg, ${color} 0%, ${color} 100%)` }
                          : undefined
                    }
                  >
                    {!cover && initials(name)}
                  </div>

                  <div className="card-actions">
                    <button
                      className="icon-btn danger"
                      title="Projeyi Sil"
                      onClick={(e) => {
                        e.stopPropagation();
                        askDeleteProject(p);
                      }}
                      disabled={deletingId === (p.uniq || p.id)}
                      type="button"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>

                  <div className="dash-body">
                    <h3>{name}</h3>
                    {p.description && <p>{p.description}</p>}
                    <div className="progress-wrapper">
                      <div
                        className="progress-bar"
                        style={{
                          width: `${progress}%`,
                          ...(color ? { background: color } : {})
                        }}
                      />
                    </div>

                    {deadline && (
                      <div
                        className={`deadline-pill ${overdue ? "past" : ""}`}
                        title={`Son tarih: ${formatTR(deadline)}`}
                      >
                        Son tarih: {formatTR(deadline)}
                        {overdue ? " • Geçti" : ""}
                      </div>
                    )}
                  </div>

                  {(p.incomplete_bugs_count || 0) !== 0 && (
                    <span
                      className={`dash-badge ${overdue ? "late" : ""}`}
                      style={
                        !overdue && color
                          ? {
                            color,
                            borderColor: badgeBorder || color,
                            backgroundColor: badgeBg
                          }
                          : undefined
                      }
                    >
                      {p.incomplete_bugs_count}
                    </span>
                  )}
                </article>
              );
            })}
        </section>

        <section className="dash-pagination" aria-label="Sayfalama">
          <button
            className="page-btn"
            onClick={() => goPage(page - 1)}
            disabled={page <= 1}
            aria-label="Önceki sayfa"
            type="button"
          >
            <FiChevronLeft size={16} />
          </button>

          {visiblePages.map((pnum, idx) => {
            const isActive = pnum === page;
            const prev = visiblePages[idx - 1];
            const needsDots = idx > 0 && pnum - prev > 1;
            return (
              <React.Fragment key={pnum}>
                {needsDots && <span className="dots">…</span>}
                <button
                  className={`page-btn ${isActive ? "active" : ""}`}
                  onClick={() => goPage(pnum)}
                  aria-current={isActive ? "page" : undefined}
                  type="button"
                >
                  {pnum}
                </button>
              </React.Fragment>
            );
          })}

          <button
            className="page-btn"
            onClick={() => goPage(page + 1)}
            disabled={page >= totalPages}
            aria-label="Sonraki sayfa"
            type="button"
          >
            <FiChevronRight size={16} />
          </button>
        </section>
      </main>

      <Modal
        open={confirmDlg.open}
        close={() => setConfirmDlg({ open: false, project: null })}
      >
        <h2>Projeyi Sil</h2>
        <p className="modal-desc">
          “{confirmDlg.project ? getProjectTitle(confirmDlg.project) : ""}” projesini silmek istediğinize emin misiniz?
        </p>
        <div className="modal-buttons">
          <button
            type="button"
            className="modal-close-button"
            onClick={() => setConfirmDlg({ open: false, project: null })}
          >
            İptal
          </button>
          <button
            type="button"
            className="modal-add-button danger"
            onClick={async () => {
              const p = confirmDlg.project;
              setConfirmDlg({ open: false, project: null });
              await handleDeleteProject(p);
            }}
          >
            Tamam
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Dashboard;
